<template>
  <div
    v-if="lazyLoaded"
    v-show="showing || (!showing && !(backdropAfterLeaveDone && modalAfterLeaveDone))"
    ref="modal"
    role="alertdialog"
    aria-modal="true"
    :aria-labelledby="title"
    :aria-describedby="contentDescription"
    tabindex="-1"
    class="fixed inset-0 h-full w-full flex items-center justify-center z-4000 touch-none focus:outline-none"
    :class="{ 'blur-desaturate': showing && lazyLoaded }"
    @keydown.tab="onTabPress"
    @keydown.esc="closeOnEscapePress ? hideEvent() : null"
  >
    <!-- Backdrop -->
    <Transition
      :name="backdropTransition"
      mode="out-in"
      appear
      @after-enter="backdropAfterEnterDone = true"
      @after-leave="backdropAfterLeaveDone = true"
    >
      <div
        v-show="showing"
        class="absolute inset-0 h-full w-full z-n1 touch-none"
        tabindex="-1"
        @click="closeOnBackdropPress ? hideEvent() : null"
      >
        <slot name="backdrop">
          <div class="h-full w-full bg-primary-dark opacity-50"></div>
        </slot>
      </div>
    </Transition>
    <!-- Modal surface (Wrapping the content) -->
    <div
      class="max-w-full w-full h-full flex items-end justify-center z-20 touch-none tablet:min-w-400 tablet:w-auto
      tablet:items-center"
      @click="closeOnBackdropPress ? hideEvent() : null"
    >
      <Transition
        :name="modalTransition"
        mode="out-in"
        appear
        @after-enter="modalAfterEnterDone = true"
        @after-leave="modalAfterLeaveDone = true"
      >
        <!-- Modal content -->
        <div
          v-show="showing"
          class="relative max-h-full max-w-500 w-full flex flex-col bg-white rounded-8 shadow-24dp overflow-hidden
          tablet:max-h-modal"
          @click.stop
        >
          <header>
            <slot
              v-if="$scopedSlots.header"
              name="header"
              :hideModal="hideEvent"
            />
            <div
              v-else
              class="relative px-50 py-18 bg-gray-200 border-b border-gray-300"
            >
              <h2 class="text-xl text-center leading-tight">
                {{ title }}
              </h2>
              <button
                type="button"
                title="Close"
                aria-label="Close"
                class="absolute top-0 right-0 h-40 w-40 flex items-center justify-center mt-10 mr-10 rounded-full
                hover:bg-gray-300 focus:outline-none"
                @click="hideEvent"
              >
                <FontAwesomeIcon
                  :icon="['fal', 'times']"
                  class="text-2xl text-gray-600"
                />
              </button>
            </div>
          </header>
          <section
            ref="content-main"
            class="relative overflow-auto scrolling-touch"
          >
            <slot :hideModal="hideEvent" />
          </section>
          <footer v-if="$scopedSlots.footer">
            <slot
              name="footer"
              :hideModal="hideEvent"
            />
          </footer>
          <!-- After (overlay, etc..) -->
          <slot
            v-if="$scopedSlots.after"
            name="after"
          />
        </div>
      </Transition>
    </div>
  </div>
</template>

<script>
import { enableBodyScroll, disableBodyScroll } from 'body-scroll-lock';
import FOCUSABLE from '@/helpers/focusable';

/**
 * A modal (Modal dialog) component for showing users content which overlays the rest of the application.
 * When opened, it traps the user's focus so that keyboard navigation will remain within the modal until it is closed.
 * It supports being closed by clicking outside the modal content or pressing the ESC key.
 */
export default {
  model: {
    prop: 'showing',
    event: 'change',
  },
  props: {
    showing: {
      type: Boolean,
      default: false,
    },
    title: {
      type: String,
      required: true,
      validator: (value) => value,
    },
    contentDescription: {
      type: String,
      required: true,
      validator: (value) => value,
    },
    backdropTransition: {
      type: String,
      default: 'fade-slow',
    },
    modalTransition: {
      type: String,
      default: 'modal',
    },
    // Behaviors
    lazyLoad: {
      type: Boolean,
      default: true,
    },
    resetScroll: {
      type: Boolean,
      default: false,
    },
    reserveScrollBarGap: {
      type: Boolean,
      default: true,
    },
    closeOnBackdropPress: {
      type: Boolean,
      default: true,
    },
    closeOnEscapePress: {
      type: Boolean,
      default: true,
    },
  },
  data: () => ({
    lazyLoaded: false,
    backdropAfterEnterDone: false,
    modalAfterEnterDone: false,
    backdropAfterLeaveDone: true,
    modalAfterLeaveDone: true,
  }),
  mounted() {
    if (!this.lazyLoad) this.lazyLoaded = true;
  },
  watch: {
    showing: {
      immediate: true,
      handler(value) {
        if (value) {
          this.lazyLoaded = true;
          // Reset after leave states when showing modal
          this.backdropAfterLeaveDone = false;
          this.modalAfterLeaveDone = false;

          this.$nextTick(() => {
            // Focus modal
            this.$refs.modal.focus();

            // Reset scroll of modal content
            if (this.resetScroll && this.$refs['content-main']) {
              this.$refs['content-main'].scrollTo(0, 0);
            }

            // Disable body scroll lock
            if (this.$refs['content-main']) {
              disableBodyScroll(this.$refs['content-main'], {
                reserveScrollBarGap: this.reserveScrollBarGap,
              });
            }

            this.showEvent();
          });
        } else {
          // Enable body scroll lock
          if (!this.$refs['content-main']) return;

          enableBodyScroll(this.$refs['content-main']);
        }
      },
    },
    transitionsAfterEnterDone: {
      handler(value) {
        if (!value) return;

        this.showingEvent();
        this.backdropAfterEnterDone = false;
        this.modalAfterEnterDone = false;
      },
    },
    transitionsAfterLeaveDone: {
      handler(value) {
        if (value) return;

        this.hidingEvent();
        this.backdropAfterLeaveDone = true;
        this.modalAfterLeaveDone = true;
      },
    },
  },
  computed: {
    transitionsAfterEnterDone() {
      return this.backdropAfterEnterDone && this.modalAfterEnterDone;
    },
    transitionsAfterLeaveDone() {
      return !(this.backdropAfterLeaveDone && this.modalAfterLeaveDone);
    },
  },
  methods: {
    showEvent() {
      /**
       * Fired when the modal opens.
       * @event show
       * @type { boolean }
       */
      this.$emit('show');
      this.$emit('change', true);
    },
    showingEvent() {
      /**
       * Fired when the modal is open and all transitions are done.
       * @event showing
       * @type { boolean }
       */
      this.$emit('shown');
    },
    hideEvent() {
      /**
       * Fired when the modal closes.
       * @event hide
       * @type { boolean }
       */
      this.$emit('hide');
      this.$emit('change', false);
    },
    hidingEvent() {
      /**
       * Fired when the modal is open and all transitions are done.
       * @event hiding
       * @type { boolean }
       */
      this.$emit('hidden');
    },
    onTabPress(event) {
      const { modal } = this.$refs;

      if (!modal) return;

      const focusable = Array.from(modal.querySelectorAll(FOCUSABLE));

      if (!focusable.length) {
        event.preventDefault();
        return;
      }
      if (!modal.contains(document.activeElement)) {
        event.preventDefault();
        focusable[0].focus();
      } else {
        const focusedItemIndex = focusable.indexOf(document.activeElement);

        if (event.shiftKey && focusedItemIndex === 0) {
          focusable[focusable.length - 1].focus();
          event.preventDefault();
        }
        if (!event.shiftKey && focusedItemIndex === focusable.length - 1) {
          focusable[0].focus();
          event.preventDefault();
        }
      }
    },
  },
};
</script>

<style>
</style>
